import React from 'react';
import Select, { components } from 'react-select';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { LANGUAGE_OPTIONS } from 'i18n/localeConfig';

import './LanguageSelector.css';

/**
 * Custom Option component for react-select that shows flag + native name.
 */
const Option = (props) => {
  const { data, isSelected } = props;
  return (
    <components.Option {...props}>
      <span className={`language-option${isSelected ? ' language-option--active' : ''}`}>
        <span className="language-option__flag" aria-hidden="true">
          {data.flag}
        </span>
        <span className="language-option__name">{data.nativeName}</span>
      </span>
    </components.Option>
  );
};

Option.propTypes = {
  data: PropTypes.shape({
    flag: PropTypes.string.isRequired,
    nativeName: PropTypes.string.isRequired,
  }).isRequired,
  isSelected: PropTypes.bool,
};

/**
 * Custom SingleValue component — shows flag only in compact mode, flag + name otherwise.
 */
const SingleValue = (props) => {
  const { data, selectProps } = props;
  const isCompact = selectProps.isCompact;

  return (
    <components.SingleValue {...props}>
      <span className="language-single-value">
        <span aria-hidden="true">{data.flag}</span>
        {!isCompact && <span className="language-single-value__name">{data.nativeName}</span>}
      </span>
    </components.SingleValue>
  );
};

SingleValue.propTypes = {
  data: PropTypes.shape({
    flag: PropTypes.string.isRequired,
    nativeName: PropTypes.string.isRequired,
  }).isRequired,
  selectProps: PropTypes.shape({
    isCompact: PropTypes.bool,
  }).isRequired,
};

/**
 * LanguageSelector — dropdown to switch the application language.
 *
 * @param {boolean} compact - When true, shows only the flag emoji (mobile-friendly).
 */
const LanguageSelector = ({ compact = false }) => {
  const { i18n } = useTranslation();

  const currentOption = LANGUAGE_OPTIONS.find((opt) => opt.value === i18n.language) || LANGUAGE_OPTIONS[0];

  const handleChange = (selected) => {
    if (selected) {
      i18n.changeLanguage(selected.value);
    }
  };

  const customStyles = {
    control: (base) => ({
      ...base,
      minHeight: compact ? 32 : 36,
      minWidth: compact ? 52 : 140,
      maxWidth: compact ? 60 : 180,
      borderRadius: 'var(--radius-sm, 6px)',
      borderColor: 'var(--color-gray-300, #dee2e6)',
      boxShadow: 'none',
      cursor: 'pointer',
      '&:hover': {
        borderColor: 'var(--color-primary, #0057b8)',
      },
    }),
    valueContainer: (base) => ({
      ...base,
      padding: compact ? '0 4px' : '0 8px',
    }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base) => ({
      ...base,
      padding: compact ? '0 2px' : '0 4px',
    }),
    menu: (base) => ({
      ...base,
      zIndex: 10001,
      minWidth: 160,
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 10001,
    }),
    option: (base, state) => {
      let backgroundColor = 'transparent';
      if (state.isSelected) {
        backgroundColor = 'var(--color-primary-light, #e8f0fe)';
      } else if (state.isFocused) {
        backgroundColor = 'var(--color-gray-100, #f8f9fa)';
      }

      return {
        ...base,
        backgroundColor,
        color: state.isSelected ? 'var(--color-primary, #0057b8)' : 'inherit',
        fontWeight: state.isSelected ? 600 : 400,
        cursor: 'pointer',
      };
    },
  };

  return (
    <div className={`language-selector${compact ? ' language-selector--compact' : ''}`}>
      <Select
        value={currentOption}
        onChange={handleChange}
        options={LANGUAGE_OPTIONS}
        components={{ Option, SingleValue }}
        styles={customStyles}
        isSearchable={false}
        isCompact={compact}
        menuPortalTarget={document.body}
        menuPlacement="auto"
        aria-label="Select language"
        classNamePrefix="lang-select"
      />
    </div>
  );
};

LanguageSelector.propTypes = {
  compact: PropTypes.bool,
};

export default LanguageSelector;
